
// ---------------------------------------------------------------------------
// API base URL — reads from Vite env, falls back to relative path (works when
// frontend and backend are served from the same origin, e.g. production).
// ---------------------------------------------------------------------------
const API_BASE =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_URL) ||
  '';

/**
 * Thin fetch wrapper that returns null on network error (triggers JSON fallback).
 */
async function apiFetch<T>(path: string): Promise<T | null> {
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      signal: AbortSignal.timeout(5000),
    });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Module-level cache — populated by initDataCache() at app startup.
// All sync methods read from here so no component code needs to change.
// ---------------------------------------------------------------------------
let _candidates: Candidate[] = [];
let _elections: Election[] = [];
let _socialPosts: SocialPost[] = [];
let _voters: Voter[] = [];
let _dailyVotesByDate: { [date: string]: DailyVote } = {};
let _socialStats: SocialStats = {};
let _trendingTopics: TrendingTopic[] = [];
let _manifestos: Manifesto[] = [];
let _manifestoCategories: ManifestoCategory[] = [];
let _manifestoStatistics: ManifestoStatistics | null = null;
let _partyColors: Record<string, string> = {};

// Static UI config — not stored in DB
const PLATFORM_COLORS: { [platform: string]: string } = {
  Twitter: '#1DA1F2',
  Facebook: '#1877F2',
  Instagram: '#E4405F',
  YouTube: '#FF0000',
  LinkedIn: '#0A66C2',
  TikTok: '#010101',
};

let _cacheReady = false;
let _cachePromise: Promise<void> | null = null;

/**
 * Pre-fetch all data from the API and warm the module cache.
 * Call this once at app startup (main.tsx). Safe to call multiple times —
 * subsequent calls return the same in-flight promise.
 */
export async function initDataCache(): Promise<void> {
  if (_cacheReady) return;
  if (_cachePromise) return _cachePromise;

  _cachePromise = (async () => {
    const [cands, elecs, posts, voters, dailyVotes, socialStatsRes, manifestosRes, parties] = await Promise.all([
      apiFetch<Candidate[]>('/api/candidates?limit=7000'),
      apiFetch<Election[]>('/api/elections?limit=500'),
      apiFetch<SocialPost[]>('/api/social-posts'),
      apiFetch<Voter[]>('/api/voters'),
      apiFetch<{ [date: string]: DailyVote }>('/api/daily-votes'),
      apiFetch<{ socialStats: SocialStats; trendingTopics: TrendingTopic[] }>('/api/social-stats'),
      apiFetch<{ manifestos: Manifesto[]; categories: ManifestoCategory[]; statistics: ManifestoStatistics }>('/api/manifestos'),
      apiFetch<{ id: string; name: string; color: string }[]>('/api/parties'),
    ]);
    if (cands?.length)          _candidates = cands;
    if (elecs?.length)          _elections = elecs;
    if (posts?.length)          _socialPosts = posts;
    if (voters?.length)         _voters = voters;
    if (dailyVotes && Object.keys(dailyVotes).length) _dailyVotesByDate = dailyVotes;
    if (socialStatsRes) {
      _socialStats = socialStatsRes.socialStats ?? {};
      _trendingTopics = socialStatsRes.trendingTopics ?? [];
    }
    if (parties?.length) {
      _partyColors = Object.fromEntries(parties.map(p => [p.id, p.color]));
    }
    if (manifestosRes) {
      _manifestos = manifestosRes.manifestos ?? [];
      _manifestoCategories = manifestosRes.categories ?? [];
      _manifestoStatistics = manifestosRes.statistics ?? null;
    }
    _cacheReady = true;
  })();

  return _cachePromise;
}

// Types
export interface Candidate {
  id: number;
  name: string;
  party: string;
  position: string;
  bio: string;
  platform: string[];
  experience: string[];
  education: string[];
  achievements: string[];
  age: number;
  state: string;
  lga: string;
  votes: number;
  percentage: number;
  socialMedia: {
    twitter?: string;
    facebook?: string;
    instagram?: string;
  };
  followers: number;
  verified: boolean;
  profileImage: string;
  website?: string;
  email?: string;
  phone?: string;
  avatar: string;
  posts: number;
  following: number;
  trend: string;
  status: string;
  election: string;
  location: string;
  supporters?: number;
  engagementRate?: number;
  // Legacy properties for compatibility
  social?: {
    twitter?: string;
    facebook?: string;
    instagram?: string;
  };
  occupation?: string;
  ward?: string;
  pollingUnit?: string;
  hasVoterCard?: boolean;
}

export interface SocialPost {
  id: string;
  platform: string;
  candidate: string;
  avatar: string;
  party: string;
  time: string;
  content: string;
  likes: number;
  comments: number;
  shares: number;
  verified: boolean;
  url: string;
  engagement: number;
  hashtags: string[];
  media?: {
    type: 'image' | 'video';
    url: string;
    caption?: string;
    thumbnail?: string;
    duration?: string;
  };
}

export interface Voter {
  id: number;
  name: string;
  avatar: string;
  verified: boolean;
  email: string;
  phone: string;
  location: string;
  registrationDate: string;
  voterId: string;
  status: string;
  voted: boolean;
  votedFor: string | null;
  age: number;
  occupation: string;
  lga: string;
  ward: string;
  pollingUnit: string;
  hasVoterCard: boolean;
  socialProfiles: {
    twitter?: string;
    linkedin?: string;
    instagram?: string;
    facebook?: string;
  };
  ipAddress: string;
}

export interface Election {
  id: number;
  title: string;
  date: string;
  type: string;
  level: 'Federal' | 'State' | 'Local';
  status: 'upcoming' | 'ongoing' | 'completed';
  candidates: number[];
  totalRegisteredVoters: number;
  totalVotesCast: number;
  turnout: number;
  states: string[];
  description: string;
  issues: string[];
  // Legacy properties for compatibility
  totalVotes?: number;
  totalVoters?: number;
  lga?: string[];
  candidatesInfo?: {
    id: number;
    name: string;
    party: string;
    votes: number;
    percentage: number;
  }[];
}

export interface DailyVote {
  id?: number;
  date?: string;
  candidates: {
    id: number;
    name: string;
    party: string;
    votes: number;
  }[];
  totalVotes: number;
  question: string;
  active: boolean;
  winner?: string;
  votes?: { [candidateId: number]: number };
  electionId?: number | null;
  electionTitle?: string | null;
  electionType?: string | null;
  electionDate?: string | null;
}

export interface SocialStats {
  [platform: string]: {
    posts: number;
    engagement: number;
    trending: boolean;
    activeUsers: number;
    reach: number;
    impressions: number;
    hashtags: string[];
  };
}

export interface TrendingTopic {
  topic: string;
  mentions: number;
  growth: number;
  platform: string;
}

export interface LgaCandidateResult {
  name: string;
  party: string;
  votes: number;
}

export interface LgaEthnicGroup {
  group: string;
  pct: number;
}

export interface LgaDetail {
  id: number;
  name: string;
  state: string;
  ward_count: number;
  polling_units: number;
  registered_voters: number;
  ethnic_groups: LgaEthnicGroup[];
  notes: string | null;
  total_votes: number;
  turnout_percentage: number;
  candidate_results: LgaCandidateResult[];
  winner: string | null;
  winner_party: string | null;
  importance_score: number;
}

export interface Manifesto {
  id: string;
  title: string;
  candidate: string;
  party: string;
  category: string;
  description: string;
  keyPoints: string[];
  avgRating: number;
  totalRatings: number;
  userRating?: number;
  votes: number;
  views: number;
  comments: number;
  lastUpdated: string;
  status: 'draft' | 'published' | 'archived';
}

export interface ManifestoCategory {
  id: string;
  name: string;
  description: string;
}

export interface ManifestoStatistics {
  totalManifestos: number;
  totalRatings: number;
  totalVotes: number;
  totalViews: number;
  totalComments: number;
  averageRating: number;
  mostRatedCategory: string;
  highestRatedManifesto: string;
  categories?: string[];
}

// Data Service
class DataService {
  // Candidates
  static getPartyColors(): Record<string, string> {
    return _partyColors;
  }

  static getPartyColor(party: string, fallback = '#666666'): string {
    return _partyColors[party] ?? fallback;
  }

  static getCandidates(): Candidate[] {
    return _candidates;
  }

  /** Async variant — fetches from API, falls back to cache. */
  static async getCandidatesAsync(filters?: { party?: string; status?: string; election?: string }): Promise<Candidate[]> {
    const params = new URLSearchParams();
    if (filters?.party)    params.set('party',    filters.party);
    if (filters?.status)   params.set('status',   filters.status);
    if (filters?.election) params.set('election', filters.election);
    const qs = params.toString();
    const data = await apiFetch<Candidate[]>(`/api/candidates${qs ? `?${qs}` : ''}`);
    return data?.length ? data : _candidates;
  }

  static getCandidateById(id: number): Candidate | undefined {
    return _candidates.find(c => c.id === id);
  }

  static async getCandidateByIdAsync(id: number): Promise<Candidate | undefined> {
    const data = await apiFetch<Candidate>(`/api/candidates/${id}`);
    return data ?? _candidates.find(c => c.id === id);
  }

  static getCandidatesByParty(party: string): Candidate[] {
    return _candidates.filter(c => c.party === party);
  }

  static getTopCandidates(limit: number = 5): Candidate[] {
    return [..._candidates]
      .sort((a, b) => b.votes - a.votes)
      .slice(0, limit);
  }

  // Social Posts
  static getSocialPosts(): SocialPost[] {
    return _socialPosts;
  }

  static getSocialPostsByPlatform(platform: string): SocialPost[] {
    return _socialPosts.filter(p => p.platform === platform);
  }

  static getSocialPostsByCandidate(candidateName: string): SocialPost[] {
    return _socialPosts.filter(p => p.candidate === candidateName);
  }

  static getTrendingPosts(limit: number = 10): SocialPost[] {
    return [..._socialPosts]
      .sort((a, b) => b.engagement - a.engagement)
      .slice(0, limit);
  }

  // Voters
  static getVoters(): Voter[] {
    return _voters;
  }

  static getVotersByStatus(status: string): Voter[] {
    return _voters.filter(v => v.status === status);
  }

  static getVotersByLga(lga: string): Voter[] {
    return _voters.filter(v => v.lga === lga);
  }

  static getVoterStats() {
    const voters = _voters;
    const total = voters.length;
    return {
      total,
      active: voters.filter(v => v.status === 'active').length,
      pending: voters.filter(v => v.status === 'pending').length,
      voted: voters.filter(v => v.voted).length,
      turnout: total > 0 ? Math.round((voters.filter(v => v.voted).length / total) * 100) : 0,
      withVoterCard: voters.filter(v => v.hasVoterCard).length,
    };
  }

  // Elections
  static getElections(): Election[] {
    return _elections;
  }

  static async getElectionsAsync(filters?: { type?: string; status?: string }): Promise<Election[]> {
    const params = new URLSearchParams();
    if (filters?.type)   params.set('type',   filters.type);
    if (filters?.status) params.set('status', filters.status);
    const qs = params.toString();
    const data = await apiFetch<Election[]>(`/api/elections${qs ? `?${qs}` : ''}`);
    return data?.length ? data : _elections;
  }

  static getElectionById(id: number): Election | undefined {
    return _elections.find(e => e.id === id);
  }

  static async getElectionResultsAsync(id: number): Promise<Election | undefined> {
    const data = await apiFetch<Election>(`/api/elections/${id}/results`);
    return data ?? _elections.find(e => e.id === id);
  }

  static async getLgasByElection(electionId: number): Promise<LgaDetail[]> {
    const res = await apiFetch<{ success: boolean; data: LgaDetail[] }>(`/api/lgas?election_id=${electionId}`);
    return res?.data ?? [];
  }

  static getElectionsByType(type: string): Election[] {
    return _elections.filter(e => e.type === type);
  }

  static getUpcomingElections(): Election[] {
    return _elections.filter(e => e.status === 'upcoming');
  }

  // Daily Votes
  static getDailyVotes(date: string): DailyVote | undefined {
    return _dailyVotesByDate[date];
  }

  static getTodayDailyVote(): DailyVote | undefined {
    const today = new Date().toISOString().split('T')[0];
    return _dailyVotesByDate[today];
  }

  static getDailyVoteHistory(): { [date: string]: DailyVote } {
    return _dailyVotesByDate;
  }

  static updateDailyVote(date: string, candidateId: number): void {
    const entry = _dailyVotesByDate[date];
    if (entry) {
      const candidate = entry.candidates.find(c => c.id === candidateId);
      if (candidate) {
        candidate.votes += 1;
        entry.totalVotes += 1;
      }
    }
  }

  // Social Stats
  static getSocialStats(): SocialStats {
    return _socialStats;
  }

  static async getSocialStatsAsync(): Promise<{
    socialStats: SocialStats;
    trendingTopics: TrendingTopic[];
  }> {
    return { socialStats: _socialStats, trendingTopics: _trendingTopics };
  }

  static getPlatformStats(platform: string) {
    return _socialStats[platform];
  }

  static getTrendingTopics(): TrendingTopic[] {
    return _trendingTopics;
  }

  static getPlatformColors(): { [platform: string]: string } {
    return PLATFORM_COLORS;
  }

  /**
   * Subscribe to live election result updates via SSE.
   * Returns an unsubscribe function.
   *
   * Example:
   *   const unsub = DataService.subscribeToResults((update) => {
   *     console.log('New results:', update);
   *   });
   *   // later:
   *   unsub();
   */
  static subscribeToResults(
    onUpdate: (payload: { electionId: number; results: unknown[] }) => void
  ): () => void {
    const url = `${API_BASE}/api/stream/results`;
    const es = new EventSource(url);

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        onUpdate(data);
      } catch {
        // ignore malformed messages
      }
    };

    es.onerror = () => {
      // Browser will automatically attempt reconnect
    };

    return () => es.close();
  }

  // Combined Data Methods
  static getOverviewStats() {
    const socialStatsKeys = Object.keys(_socialStats);
    return {
      totalCandidates: _candidates.length,
      activeVoters: _voters.filter(v => v.status === 'active').length,
      ongoingElections: _elections.filter(e => e.status === 'upcoming').length,
      totalVotes: _candidates.reduce((sum, c) => sum + c.votes, 0),
      engagement: socialStatsKeys.length > 0
        ? Math.round(
            Object.values(_socialStats).reduce((sum, s) => sum + s.engagement, 0) / socialStatsKeys.length
          )
        : 0,
      totalSocialPosts: _socialPosts.length,
      averageEngagement: _socialPosts.length > 0
        ? Math.round(
            _socialPosts.reduce((sum, p) => sum + p.likes + p.shares, 0) / _socialPosts.length
          )
        : 0,
      topIssues: ['Economy', 'Security', 'Healthcare', 'Education', 'Infrastructure']
    };
  }

  static getAggregatedFeed(limit: number = 20): SocialPost[] {
    return [..._socialPosts]
      .sort((a, b) => b.engagement - a.engagement)
      .slice(0, limit);
  }

  static getElectionInsights() {
    return {
      totalElections: _elections.length,
      upcomingElections: _elections.filter(e => e.status === 'upcoming').length,
      averageTurnout: _elections.length > 0
        ? _elections.reduce((sum, e) => sum + e.turnout, 0) / _elections.length
        : 0,
      topIssues: this.getTopIssues(),
      candidatePerformance: _candidates.map(c => ({
        name: c.name,
        party: c.party,
        votes: c.votes,
        percentage: c.percentage,
        trend: c.trend
      }))
    };
  }

  private static getTopIssues(): string[] {
    const allIssues = _elections.flatMap(e => e.issues ?? []);
    const issueCount = allIssues.reduce((acc: { [key: string]: number }, issue) => {
      acc[issue] = (acc[issue] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(issueCount)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([issue]) => issue);
  }

  // Manifestos
  static getManifestos(): Manifesto[] {
    return _manifestos;
  }

  static getManifestoById(id: string): Manifesto | undefined {
    return _manifestos.find(m => m.id === id);
  }

  static getManifestosByCategory(category: string): Manifesto[] {
    return _manifestos.filter(m => m.category === category);
  }

  static getManifestosByCandidate(candidate: string): Manifesto[] {
    return _manifestos.filter(m => m.candidate === candidate);
  }

  static getManifestoCategories(): ManifestoCategory[] {
    return _manifestoCategories;
  }

  static getManifestoStatistics(): ManifestoStatistics {
    return _manifestoStatistics ?? {
      totalManifestos: 0,
      totalRatings: 0,
      totalVotes: 0,
      totalViews: 0,
      totalComments: 0,
      averageRating: 0,
      mostRatedCategory: '',
      highestRatedManifesto: '',
    };
  }

  static getTopRatedManifestos(limit: number = 5): Manifesto[] {
    return [..._manifestos]
      .sort((a, b) => b.avgRating - a.avgRating)
      .slice(0, limit);
  }

  static getMostDiscussedManifestos(limit: number = 5): Manifesto[] {
    return [..._manifestos]
      .sort((a, b) => b.comments - a.comments)
      .slice(0, limit);
  }

  static searchManifestos(query: string): Manifesto[] {
    const lowercaseQuery = query.toLowerCase();
    return _manifestos.filter(m =>
      m.title.toLowerCase().includes(lowercaseQuery) ||
      m.description.toLowerCase().includes(lowercaseQuery) ||
      m.candidate.toLowerCase().includes(lowercaseQuery) ||
      m.party.toLowerCase().includes(lowercaseQuery) ||
      m.keyPoints.some(point => point.toLowerCase().includes(lowercaseQuery))
    );
  }

  /** Returns cached social posts (populated by initDataCache). */
  static async getSocialPostsAsync(_filters?: { platform?: string; candidate?: string }): Promise<SocialPost[]> {
    return _socialPosts;
  }

  /** Fetches per-candidate per-platform social trends from the /api/social-trends endpoint, falls back to cache. */
  static async getSocialTrendsAsync(): Promise<SocialTrend[]> {
    const data = await apiFetch<SocialTrend[]>('/api/social-trends');
    if (data?.length) return data;

    // Fallback: compute trends from cached social posts
    const posts = _socialPosts;
    if (!posts?.length) return [];
    const map = new Map<string, SocialTrend>();
    for (const p of posts) {
      const key = `${p.platform}-${p.candidate}`;
      const existing = map.get(key);
      const mentions = (p.likes || 0) + (p.shares || 0);
      if (existing) {
        existing.mentions += mentions;
      } else {
        map.set(key, {
          id: key,
          platform: p.platform,
          candidate: p.candidate,
          party: p.party,
          mentions,
          percentage: 0,
          sentiment: 'neutral',
          date: new Date().toISOString(),
        });
      }
    }
    const trends = Array.from(map.values());
    const totalMentions = trends.reduce((s, t) => s + t.mentions, 0);
    for (const t of trends) {
      t.percentage = totalMentions > 0 ? Math.round((t.mentions / totalMentions) * 100) : 0;
    }
    return trends;
  }

  /** Returns cached voters (populated by initDataCache). */
  static async getVotersAsync(): Promise<Voter[]> {
    return _voters;
  }

  /** Fetches winning candidate per state-level (Gubernatorial) election, falls back to cached elections. */
  static async getStateResultsAsync(): Promise<StateResult[]> {
    const data = await apiFetch<StateResult[]>('/api/state-results');
    if (data?.length) return data;

    // Fallback: derive state results from cached elections
    const elections = _elections;
    return elections
      .filter((e: any) => e.type === 'Gubernatorial' && e.candidates?.length)
      .map((e: any) => {
        const top = [...(e.candidates || [])].sort((a: any, b: any) => (b.percentage || 0) - (a.percentage || 0))[0];
        const stateName = (e.title || '')
          .replace(/Governorship Election - /i, '')
          .replace(/ State \d{4}$/, '')
          .replace(/ \d{4}$/, '')
          .trim();
        return {
          state: stateName || e.state || 'Unknown',
          leadingCandidate: top?.name || 'TBD',
          party: top?.party || '',
          percentage: top?.percentage || 0,
          turnout: e.turnout || 0,
          status: e.status || 'unknown',
        };
      })
      .filter((r: StateResult) => r.state !== 'Unknown');
  }

  // -------------------------------------------------------------------------
  // Stears / Historical election data
  // -------------------------------------------------------------------------

  /** State-by-state presidential results from Stears Elections. */
  static async getStearStateResults(filters?: { year?: number; state?: string }): Promise<StearsStateResult[]> {
    const params = new URLSearchParams();
    if (filters?.year)  params.set('year', String(filters.year));
    if (filters?.state) params.set('state', filters.state);
    const qs = params.toString();
    const data = await apiFetch<StearsStateResult[]>(`/api/stears/state-results${qs ? `?${qs}` : ''}`);
    return data ?? [];
  }

  /** Party vote totals per state with winning party. */
  static async getStearStateSummaries(filters?: { year?: number; state?: string }): Promise<StearsStateSummary[]> {
    const params = new URLSearchParams();
    if (filters?.year)  params.set('year', String(filters.year));
    if (filters?.state) params.set('state', filters.state);
    const qs = params.toString();
    const data = await apiFetch<StearsStateSummary[]>(`/api/stears/state-summaries${qs ? `?${qs}` : ''}`);
    return data ?? [];
  }

  /** Voter metrics per election year (registered, turnout, invalid). */
  static async getStearVoterMetrics(year?: number): Promise<StearsVoterMetrics[]> {
    const qs = year ? `?year=${year}` : '';
    const data = await apiFetch<StearsVoterMetrics[]>(`/api/stears/voter-metrics${qs}`);
    return data ?? [];
  }

  /** Historical state-level presidential results (2011-2023). */
  static async getHistoricalStateResults(filters?: { election_id?: number; state?: string }): Promise<HistoricalStateResult[]> {
    const params = new URLSearchParams();
    if (filters?.election_id) params.set('election_id', String(filters.election_id));
    if (filters?.state)       params.set('state', filters.state);
    const qs = params.toString();
    const data = await apiFetch<HistoricalStateResult[]>(`/api/historical/state-results${qs ? `?${qs}` : ''}`);
    return data ?? [];
  }

  /** Aggregated election overview stats. Falls back to deriving from state-results + candidates. */
  static async getElectionOverview(year?: number): Promise<ElectionOverview | null> {
    const qs = year ? `?year=${year}` : '';
    const data = await apiFetch<ElectionOverview>(`/api/election-overview${qs}`);
    if (data) return data;

    // Derive from available data — known 2023 presidential election figures
    const stateResults = await DataService.getStateResultsAsync();
    const candidates = _candidates.length ? _candidates : (await apiFetch<any[]>('/api/candidates?limit=500')) ?? [];
    const targetYear = year ?? 2023;
    const uniqueStates = new Set(stateResults.map(r => r.state));
    const totalStates = uniqueStates.size || 37;
    const completedStates = stateResults.filter(r => r.status === 'completed').length || totalStates;
    const avgTurnout = stateResults.length
      ? stateResults.reduce((s, r) => s + (r.turnout || 0), 0) / stateResults.length
      : 26.72;
    const totalVotesCast = candidates.reduce((s: number, c: any) => s + (c.votes || 0), 0);
    // Nigeria INEC 2023: 93,469,008 registered voters
    const REGISTERED_2023 = 93_469_008;

    return {
      year: targetYear,
      totalStates,
      registeredVoters: REGISTERED_2023,
      eligiblePopulation: 220_000_000,
      totalVotesCast: totalVotesCast || 24_000_000,
      turnoutPct: parseFloat(avgTurnout.toFixed(2)),
      invalidVotes: 0,
      invalidPct: 0,
      resultsReported: completedStates,
    };
  }
}

export default DataService;

// ---------------------------------------------------------------------------
// Exported types for cross-component use
// ---------------------------------------------------------------------------

export interface SocialTrend {
  id: string;
  platform: string;
  candidate: string;
  party: string;
  mentions: number;
  percentage: number;
  sentiment: 'positive' | 'neutral' | 'negative';
  date: string;
}

export interface StateResult {
  state: string;
  leadingCandidate: string;
  party: string;
  percentage: number;
  turnout: number;
  status: string;
}

export interface StearsStateResult {
  id: number;
  year: number;
  election_type: string;
  election_date: string;
  state: string;
  candidate: string;
  party: string;
  votes: number;
  percentage: number;
  source: string;
}

export interface StearsStateSummary {
  id: number;
  year: number;
  election_type: string;
  state: string;
  winning_party: string;
  pdp_votes: number;
  apc_votes: number;
  nnpp_votes: number;
  lp_votes: number;
  other_votes: number;
  total_votes: number;
}

export interface StearsVoterMetrics {
  id: number;
  year: number;
  election_type: string;
  registered_voters: number;
  total_votes: number;
  turnout_pct: number;
  eligible_population: number;
  invalid_votes: number;
  invalid_pct: number;
}

export interface HistoricalStateResult {
  id: number;
  state: string;
  votes: number;
  total_votes_in_state: number;
  turnout_pct: number;
  election_id: number;
  election_title: string;
  election_date: string;
  candidate_id: number;
  candidate_name: string;
  party: string;
}

export interface ElectionOverview {
  year: number;
  totalStates: number;
  registeredVoters: number;
  eligiblePopulation: number;
  totalVotesCast: number;
  turnoutPct: number;
  invalidVotes: number;
  invalidPct: number;
  resultsReported: number;
}
